'use client'

/**
 * FO-307-A1 - OfflineProvider: online-status, wachtrij-tellers en de
 * `runMutation`-seam waarmee de veld-flows offline-aware worden.
 *
 * - Online: de mutatie gaat direct de deur uit; een conflict (409) of fout komt
 *   meteen terug bij de aanroeper.
 * - Offline (of netwerk valt weg tijdens verzenden): de mutatie gaat in de
 *   IndexedDB-wachtrij en wordt bij herstel van verbinding afgespeeld
 *   (online-event + Background Sync + periodiek als vangnet).
 */
import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
  type ReactNode,
} from 'react'
import { classifySyncOutcome } from '@streep/utils'
import { buildMutation, listMutations, saveMutation, discardMutation, type MutationDescriptor } from './queue'
import { dispatchMutation } from './dispatcher'
import { processQueue } from './sync'
import type { QueuedMutation } from './types'

const SYNC_TAG = 'streep-offline-mutations'

export interface RunMutationResult {
  ok?: boolean
  queued?: boolean
  conflict?: boolean
  error?: string
}

interface OfflineContextValue {
  online: boolean
  pending: number
  conflicts: number
  failed: number
  syncing: boolean
  items: QueuedMutation[]
  runMutation: (desc: MutationDescriptor) => Promise<RunMutationResult>
  sync: () => Promise<void>
  refresh: () => Promise<void>
  discard: (id: string) => Promise<void>
}

const OfflineContext = createContext<OfflineContextValue | null>(null)

export function OfflineProvider({ children }: { children: ReactNode }) {
  const [online, setOnline] = useState(true)
  const [items, setItems] = useState<QueuedMutation[]>([])
  const [syncing, setSyncing] = useState(false)
  const syncingRef = useRef(false)

  const refresh = useCallback(async () => {
    const all = await listMutations()
    setItems(all)
  }, [])

  const sync = useCallback(async () => {
    if (syncingRef.current) return
    if (typeof navigator !== 'undefined' && !navigator.onLine) return
    syncingRef.current = true
    setSyncing(true)
    try {
      await processQueue()
    } finally {
      syncingRef.current = false
      setSyncing(false)
      await refresh()
    }
  }, [refresh])

  const runMutation = useCallback(
    async (desc: MutationDescriptor): Promise<RunMutationResult> => {
      const mutation = buildMutation(desc)
      const isOnline = typeof navigator === 'undefined' ? true : navigator.onLine
      if (!isOnline) {
        await saveMutation(mutation)
        await refresh()
        return { queued: true }
      }
      const { outcome, error } = await dispatchMutation(mutation)
      const decision = classifySyncOutcome(outcome)
      if (decision === 'success') {
        void refresh()
        return { ok: true }
      }
      if (decision === 'retry') {
        // Verbinding viel weg tijdens verzenden -> bewaren voor een latere poging.
        await saveMutation(mutation)
        await refresh()
        return { queued: true }
      }
      if (decision === 'conflict') return { ok: false, conflict: true, error }
      return { ok: false, error }
    },
    [refresh],
  )

  const discard = useCallback(
    async (id: string) => {
      await discardMutation(id)
      await refresh()
    },
    [refresh],
  )

  // Init + event-listeners.
  useEffect(() => {
    setOnline(typeof navigator === 'undefined' ? true : navigator.onLine)
    void refresh()

    const onOnline = () => {
      setOnline(true)
      void sync()
    }
    const onOffline = () => setOnline(false)
    const onQueueChanged = () => {
      void refresh()
    }
    window.addEventListener('online', onOnline)
    window.addEventListener('offline', onOffline)
    window.addEventListener('offline-queue-changed', onQueueChanged)

    // Background Sync registreren (best-effort) + luisteren of de SW ons wekt.
    let onSwMessage: ((e: MessageEvent) => void) | null = null
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready
        // `reg.sync` bestaat niet op iOS -> de optional-chain + catch vangt dat af;
        // we leunen dan op het online-event + het periodieke vangnet.
        .then((reg) => reg.sync?.register(SYNC_TAG))
        .catch(() => {})
      onSwMessage = (e: MessageEvent) => {
        if (e.data === 'offline-sync') void sync()
      }
      navigator.serviceWorker.addEventListener('message', onSwMessage)
    }

    // Periodiek vangnet: elke 45s afspelen zolang er iets in de wachtrij staat.
    const interval = window.setInterval(() => {
      if (navigator.onLine) void sync()
    }, 45_000)

    return () => {
      window.removeEventListener('online', onOnline)
      window.removeEventListener('offline', onOffline)
      window.removeEventListener('offline-queue-changed', onQueueChanged)
      if (onSwMessage) navigator.serviceWorker.removeEventListener('message', onSwMessage)
      window.clearInterval(interval)
    }
  }, [refresh, sync])

  const pending = items.filter((m) => m.status === 'pending' || m.status === 'syncing').length
  const conflicts = items.filter((m) => m.status === 'conflict').length
  const failed = items.filter((m) => m.status === 'failed').length

  const value: OfflineContextValue = {
    online,
    pending,
    conflicts,
    failed,
    syncing,
    items,
    runMutation,
    sync,
    refresh,
    discard,
  }

  return <OfflineContext.Provider value={value}>{children}</OfflineContext.Provider>
}

/** Toegang tot de offline-status + acties. Buiten de provider: veilige no-op-defaults. */
export function useOffline(): OfflineContextValue {
  const ctx = useContext(OfflineContext)
  if (!ctx) {
    return {
      online: true,
      pending: 0,
      conflicts: 0,
      failed: 0,
      syncing: false,
      items: [],
      runMutation: async () => ({ ok: true }),
      sync: async () => {},
      refresh: async () => {},
      discard: async () => {},
    }
  }
  return ctx
}
